import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  CheckCircle,
  Clock,
  AlertCircle,
  FileCheck,
  UploadIcon,
  DeleteIcon,
  TrashIcon,
} from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
} from "../../components/ui/card";
import { useDropzone } from "react-dropzone";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import MagicLinkForm from "../../components/claimant/magic-link-form";
import IssueTypeSelector from "../../components/claimant/issue-type-selector";
import {
  GetClaimDetailApi,
  CreateMinimalClaimApi,
  UpdateClaimApi,
} from "../Claims/ClaimsAPI";
import ChatInterface from "../../components/claimant/chat-interface";
function CreateTicket() {
  const { planId: urlPlanId } = useParams();
  const [currentStep, setCurrentStep] = useState("plan-verification");
  const [planDetails, setPlanDetails] = useState(null);
  const [claimDetails, setClaimDetails] = useState(null);
  const [selectedIssueType, setSelectedIssueType] = useState("");
  const [issueDescription, setIssueDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [demoProgress, setDemoProgress] = useState(1);
  const [verifiedPlan, setVerifiedPlan] = useState("");
  const [files, setFiles] = useState([]);
  const dispatch = useDispatch();
  const demoSteps = [
    {
      id: 1,
      title: "Plan Verification",
      description: "Enter your protection plan ID to verify coverage",
      status:
        currentStep === "plan-verification"
          ? "current"
          : planDetails || claimDetails
          ? "completed"
          : "pending",
    },
    {
      id: 2,
      title: "Issue Selection",
      description: "Choose the type of issue you're experiencing",
      status:
        currentStep === "issue-selection"
          ? "current"
          : selectedIssueType
          ? "completed"
          : planDetails
          ? "pending"
          : "pending",
    },
    {
      id: 3,
      title: "Issue Description",
      description: "Describe your issue in detail",
      status:
        currentStep === "issue-description"
          ? "current"
          : issueDescription
          ? "completed"
          : selectedIssueType
          ? "pending"
          : "pending",
    },
    {
      id: 4,
      title: "Troubleshoot & Evidence Upload",
      description: "Upload photos, documents, or record audio evidence",
      status:
        currentStep === "evidence-upload"
          ? "current"
          : currentStep === "claim-submission"
          ? "completed"
          : "pending",
    },
    {
      id: 5,
      title: "Claim Submission",
      description: "Review and submit your claim for processing",
      status:
        currentStep === "claim-submission"
          ? "current"
          : submissionResult
          ? "completed"
          : "pending",
    },
  ];

  const handlePlanVerified = (verifiedPlan) => {
    setPlanDetails(verifiedPlan);
    setCurrentStep("issue-selection");
    setDemoProgress(2);
  };

  const handleIssueTypeSelected = (issueType) => {
    setSelectedIssueType(
      typeof issueType === "object" ? issueType.label : issueType
    );
    setCurrentStep("issue-description");
    setDemoProgress(3);
  };

  const advanceToEvidenceUpload = () => {
    setCurrentStep("evidence-upload");
    setDemoProgress(4);
  };

  const handleSubmitClaim = async () => {
    setIsSubmitting(true);
    try {
      const payload = {
        issueType: selectedIssueType,
        description: issueDescription,
      };
      let response;
      if (urlPlanId) {
        payload.claimId = claimDetails?.claimID;
        payload.status = "Submitted";
        payload.claimAmount = 0; // Set to 0 for demo
        response = await dispatch(UpdateClaimApi(payload));
      } else {
        payload.planID = planDetails.planID;
        response = await dispatch(CreateMinimalClaimApi(payload));
      }
      if (response.success) {
        setSubmissionResult(response.data);
        setCurrentStep("claim-submission");
        setDemoProgress(5);
      } else {
        console.error("Error submitting claim:", response.error);
      }
    } catch (error) {
      console.error("Error submitting claim:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetDemo = () => {
    setCurrentStep("plan-verification");
    setPlanDetails(null);
    setSelectedIssueType("");
    setIssueDescription("");
    setSubmissionResult(null);
    setDemoProgress(1);
  };

  const getStepIcon = (step) => {
    switch (step.status) {
      case "completed":
        return <CheckCircle className="text-green-500" size={20} />;
      case "current":
        return <Clock className="text-blue-500" size={20} />;
      default:
        return <AlertCircle className="text-slate-400" size={20} />;
    }
  };
  const onDrop = useCallback((acceptedFiles) => {
    const mappedFiles = acceptedFiles.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      })
    );
    setFiles((prev) => [...prev, ...mappedFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/*": [],
      "application/pdf": [],
      "application/msword": [],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [],
      "video/*": [],
    },
    multiple: true,
  });

  const removeFile = (name) => {
    setFiles((prev) => prev.filter((file) => file.name !== name));
  };
  useEffect(() => {
    if (urlPlanId && currentStep === "plan-verification" && !planDetails) {
      const verifyPlanFromUrl = async () => {
        try {
          const response = await GetClaimDetailApi(urlPlanId.toString());

          if (response.success && response.payload) {
            setClaimDetails(response.payload);
            setCurrentStep("issue-selection");
            setDemoProgress(2);
          } else {
            console.error("Failed to verify plan from URL:", response.error);
          }
        } catch (error) {
          console.error("Error verifying plan from URL:", error);
        }
      };

      verifyPlanFromUrl();
    }
  }, [urlPlanId, currentStep, planDetails]);
  return (
    <>
      <div className="max-w-6xl mx-auto !p-6 !space-y-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="!text-2xl">Complete Claim</CardTitle>
                <p className="text-slate-600 mt-2">
                  Experience the full step 1-5 claim process
                </p>
              </div>
              {/* <Button variant="outline" onClick={resetDemo}>
                Reset Claim
              </Button> */}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 max-w-5xl mx-auto">
              {demoSteps.map((step) => (
                <div
                  key={step.id}
                  className={`p-3 rounded-lg border-2 transition-all min-h-[110px] flex flex-col ${
                    step.status === "current"
                      ? "border-blue-500 bg-blue-50"
                      : step.status === "completed"
                      ? "border-green-500 bg-green-50"
                      : "border-slate-200 bg-slate-50"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex-shrink-0">{getStepIcon(step)}</div>
                    <Badge
                      variant={
                        step.status === "completed" ? "default" : "outline"
                      }
                      className="text-xs flex-shrink-0"
                    >
                      Step {step.id}
                    </Badge>
                  </div>
                  <h3 className="font-semibold text-sm leading-tight mb-1">
                    {step.title}
                  </h3>
                  <p className="text-xs text-slate-600 leading-relaxed flex-1">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="max-w-4xl mx-auto">
          {currentStep === "plan-verification" && (
            <Card>
              <CardHeader>
                <CardTitle>Step 1: Plan Verification</CardTitle>
                <p className="text-slate-600">
                  Enter your protection plan ID to get started
                </p>
              </CardHeader>
              <CardContent>
                <MagicLinkForm onPlanVerified={handlePlanVerified} />
                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-semibold text-blue-900 mb-2">
                    Demo Plan IDs:
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span>Example Protection Plan:</span>
                      <code className="bg-white px-2 py-1 rounded">
                        CP-2824-789123
                      </code>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {currentStep === "issue-selection" &&
            (planDetails || claimDetails) && (
              <Card>
                <CardHeader>
                  <CardTitle>Step 2: Select Issue Type</CardTitle>
                  <p className="text-slate-600">
                    Choose the type of issue you're experiencing
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="mb-6 p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-semibold mb-2">Plan Details</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-slate-500">Plan ID:</span>
                        <p className="font-medium">
                          {claimDetails
                            ? claimDetails.plan.businessPlanID
                            : planDetails.businessPlanID}
                        </p>
                      </div>
                      <div>
                        <span className="text-slate-500">Product:</span>
                        <p className="font-medium">
                          {claimDetails
                            ? claimDetails.plan.productName
                            : planDetails.productName}
                        </p>
                      </div>
                      <div>
                        <span className="text-slate-500">Customer:</span>
                        <p className="font-medium">
                          {claimDetails
                            ? claimDetails.customer.fullName
                            : planDetails.customer.fullName}
                        </p>
                      </div>
                      <div>
                        <span className="text-slate-500">Status:</span>
                        <p className="font-medium">
                          {claimDetails
                            ? claimDetails.plan.planStatus
                            : planDetails.status}
                        </p>
                      </div>
                    </div>
                  </div>

                  <IssueTypeSelector
                    onIssueTypeSelected={handleIssueTypeSelected}
                  />
                </CardContent>
              </Card>
            )}

          {currentStep === "issue-description" && (
            <Card>
              <CardHeader>
                <CardTitle>Step 3: Describe Your Issue</CardTitle>
                <p className="text-slate-600">
                  Provide details about the problem you're experiencing
                </p>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <Textarea
                    placeholder="Describe the problem you're facing in detail..."
                    className="min-h-[150px]"
                    value={issueDescription}
                    onChange={(e) => setIssueDescription(e.target.value)}
                  />
                </div>
                <div className="flex justify-end">
                  <Button onClick={advanceToEvidenceUpload}>
                    Continue to Evidence Upload →
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {currentStep === "evidence-upload" && (
            <Card>
              <CardHeader>
                <CardTitle>Step 4: Upload Evidence</CardTitle>
                <p className="text-slate-600">
                  Add supporting evidence for your claim (optional for demo)
                </p>
              </CardHeader>
              <CardContent>
                <div className="mb-6">
                  <ChatInterface key={verifiedPlan} planId={verifiedPlan} />
                </div>
                {/* <div className="w-full" style={{ width: "100%" }}>
                 
                  <div
                    {...getRootProps()}
                    className="border-2 border-dashed border-gray-300 p-6 rounded-lg text-center cursor-pointer hover:border-blue-500 transition"
                  >
                    <input {...getInputProps()} />
                    {isDragActive ? (
                      <>
                        <UploadIcon className="mx-auto mt-1" />
                        <p className="text-blue-600">Drop the files here ...</p>
                      </>
                    ) : (
                      <>
                        <UploadIcon className="mx-auto" />
                        <p className="text-gray-600 mt-1">
                          Drag & drop files here, or click to browse
                        </p>
                      </>
                    )}
                  </div>

                
                  {files.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {files.map((file) => (
                        <div
                          key={file.name}
                          className="flex items-center justify-between p-1 border rounded-lg"
                        >
                          <div className="flex items-center space-x-3">
                            {file.type.startsWith("image/") ? (
                              <img
                                src={file.preview}
                                alt={file.name}
                                className="w-16 h-16 object-cover rounded"
                              />
                            ) : file.type === "application/pdf" ? (
                              <div className="w-16 h-16 flex items-center justify-center bg-red-100 rounded text-red-600 font-bold">
                                PDF
                              </div>
                            ) : file.type.startsWith("video/") ? (
                              <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded text-blue-600 font-bold">
                                🎥
                              </div>
                            ) : (
                              <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded text-gray-600 font-bold">
                                DOC
                              </div>
                            )}
                            <div>
                              <p className="text-sm font-medium">{file.name}</p>
                              <p className="text-xs text-gray-500">
                                {(file.size / 1024).toFixed(2)} KB
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => removeFile(file.name)}
                            className="text-red-600 text-sm hover:underline"
                            style={{ marginRight: 8 }}
                          >
                            <TrashIcon size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div> */}

                <div className="flex justify-between mt-6">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentStep("issue-description")}
                  >
                    ← Back
                  </Button>
                  <Button onClick={() => setCurrentStep("claim-submission")}>
                    Continue to Submission →
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {currentStep === "claim-submission" && (
            <Card>
              <CardHeader>
                <CardTitle>Step 5: Review & Submit</CardTitle>
                <p className="text-slate-600">
                  Review your claim details and submit
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4 mb-6">
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-semibold mb-2">Claim Summary</h4>
                    <div className="text-sm space-y-3">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Plan ID:</span>
                        <span className="font-medium">
                          {claimDetails
                            ? claimDetails.plan.businessPlanID
                            : planDetails.businessPlanID}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Product:</span>
                        <span className="font-medium">
                          {claimDetails
                            ? claimDetails.plan.productName
                            : planDetails.productName}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Issue Type:</span>
                        <span className="font-medium">{selectedIssueType}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">
                          Issue Description:
                        </span>
                        <span className="font-medium text-right max-w-[60%]">
                          {issueDescription || "Not provided"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {!submissionResult ? (
                    <Button
                      className="w-full bg-green-600 hover:bg-green-700"
                      onClick={handleSubmitClaim}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Submitting..." : "Submit Claim"}
                    </Button>
                  ) : (
                    <div className="text-center p-6 bg-green-50 border border-green-200 rounded-lg">
                      <CheckCircle
                        className="text-green-500 mx-auto mb-3"
                        size={48}
                      />
                      <h3 className="text-lg font-semibold text-green-900 mb-2">
                        Claim Submitted Successfully!
                      </h3>
                      <p className="text-green-700 mb-4">
                        Your claim {submissionResult.businessClaimID} has been
                        submitted and is under review.
                      </p>
                      {/* <Button variant="outline" onClick={resetDemo}>
                        Start New Claim
                      </Button> */}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}

export default CreateTicket;
